import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { ParserHlp } from 'src/lib/fliko/helpers/parser.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { CompanyData } from 'src/lib/fliko/models/company/company.data';
import { CompanyEmployeeData } from 'src/lib/fliko/models/company/employee.data';
import { ContactDictData } from 'src/lib/fliko/models/dictionary.data';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { EstateCollection, EstateObject, WholeCollection } from 'src/lib/fliko/models/http/superAdminRequest.data';
import { RentalUnit } from 'src/lib/fliko/models/units/unit.data';
import { EstateAdminData, UserType } from 'src/lib/fliko/models/users/user.data';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { AsyncHlp } from 'src/app/shared/helpers/async.helper';
import { ViewModelHelper } from 'src/app/shared/helpers/viewModel.helper';
import { idMap, idMapArray } from 'src/app/shared/operators/idMap.operator';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { SubcontractorsService } from 'src/app/view/estate-view/services/subcontractors.service';
import { UnitAccessVM } from 'src/app/view/user-context/services/models/estateUser.data';
import { UserService } from 'src/app/view/users/services/user.service';
import { AdminService } from './admin.service';
import { EstateCoordVM, ResidentEmailVM, ResidentPhoneVM, ServiceUserVM, UserViewModel } from './models/view-models';

export type DSUpdate = EstateCollection | EstateObject | WholeCollection;

@Injectable({ providedIn: 'root' })
export class AdminDataService implements OnDestroy {
  private _emails$: BehaviorSubject<ResidentEmailVM[]> | null = null;
  private _phones$: BehaviorSubject<ResidentPhoneVM[]> | null = null;
  private _estates$ = new BehaviorSubject<EstateData[]>([]);
  private _estateAdmins$: BehaviorSubject<EstateAdminData[]> | null = null;
  private _serviceUsers$ = new BehaviorSubject<ServiceUserVM[]>([]);
  private _companies$: BehaviorSubject<CompanyData[]> | null = null;
  private _usersVM$: BehaviorSubject<UserViewModel[]> | null = null;
  private _estateUserMap$: BehaviorSubject<Map<string, UnitAccessVM[]>> | null = null;
  private _units$ = new Map<string, BehaviorSubject<Map<string, RentalUnit> | null>>();

  private subscription$ = new Subscription();
  constructor(
    private adminService: AdminService,
    private userService: UserService,
    private subcontractorService: SubcontractorsService,
    private ls: LocalizationService
  ) {}

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public getEstates$(): Observable<EstateData[]> {
    if (this._estates$.value.length == 0) {
      this.subscription$.add(this.adminService.getEstates$().subscribe((estates) => this._estates$.next(estates)));
    }

    return this._estates$.asObservable();
  }

  public getEstateAdmins$(): Observable<EstateAdminData[]> {
    if (!this._estateAdmins$) {
      this._estateAdmins$ = new BehaviorSubject<EstateAdminData[]>([]);
      this.adminService.getEstateAdmins().then((data) => AsyncHlp.updateBS(this._estateAdmins$, data));
    }
    return this._estateAdmins$.asObservable();
  }

  public getEmailsVM$(): Observable<ResidentEmailVM[]> {
    if (!this._emails$) {
      this._emails$ = new BehaviorSubject<ResidentEmailVM[]>([]);
      this.prepareEmailsVM();
    }

    return this._emails$.asObservable();
  }

  public getPhonesVM$(): Observable<ResidentPhoneVM[]> {
    if (!this._phones$) {
      this._phones$ = new BehaviorSubject<ResidentPhoneVM[]>([]);
      this.preparePhonesVM();
    }
    return this._phones$.asObservable();
  }

  public getServiceUsersVM$(): Observable<ServiceUserVM[]> {
    if (this._serviceUsers$.value.length == 0) this.prepareServiceUsersVM();
    return this._serviceUsers$.asObservable();
  }

  public getUsersVMs$(): Observable<UserViewModel[]> {
    if (!this._usersVM$) {
      this._usersVM$ = new BehaviorSubject<UserViewModel[]>([]);
      this.subscription$.add(
        combineLatest([
          this.getUserAccessVMMap$(),
          this.userService.getUsers$(),
          this.getEstates$().pipe(idMap()),
          this.getEstateAdmins$().pipe(idMapArray(['userId'])),
          this.adminService.getEmployees$().pipe(idMap())
        ]).subscribe(async ([accessMap, users, estateMap, estateAdminsMap, employeeMap]) => {
          const usersVM: UserViewModel[] = [];
          const companyEmployees = await this.adminService.getCompanyEmployees();
          const companyEmpMap = new Map<string, CompanyEmployeeData[]>();
          for (const companyId in companyEmployees) {
            for (const userId in companyEmployees[companyId]) {
              const emp = companyEmployees[companyId][userId];
              MapHlp.arrayMapAdd(companyEmpMap, userId, emp);
            }
          }
          users.forEach((user) => {
            const employee = employeeMap.get(user.id);
            const access = accessMap.get(user.id);
            const admins = estateAdminsMap.get(user.id);

            const estateIdsSet = new Set<string>();
            access?.forEach((a) => {
              if (a.estateId) estateIdsSet.add(a.estateId);
            });

            // ? searching in estate map to omit errors with undefined estate
            const estateIds: string[] = [];
            estateIdsSet.forEach((id) => {
              if (estateMap.get(id)) estateIds.push(id);
            });
            usersVM.push({
              id: user.id,
              data: user,
              employee: employee ?? null,
              estateAdmins: admins ?? [],
              companyEmployees: companyEmpMap.get(user.id) ?? [],
              isAdmin: !!admins?.length,
              unitAccess: access ?? [],
              estateIds
            });
          });
          this._usersVM$!.next(usersVM);
        })
      );
    }
    return this._usersVM$.asObservable();
  }

  public getUserVM$(userId: string): Observable<UserViewModel | null> {
    return this.getUsersVMs$().pipe(map((users) => users.find((u) => u.id === userId) ?? null));
  }

  private getUserAccessVMMap$(): Observable<Map<string, UnitAccessVM[]>> {
    if (!this._estateUserMap$) {
      this._estateUserMap$ = new BehaviorSubject(new Map());
      this.initUserAccessVMMap();
    }
    return this._estateUserMap$.asObservable();
  }

  private initUserAccessVMMap(): void {
    this.adminService.getEstateUsersColObj().then((obj) => {
      const userMap = new Map<string, UnitAccessVM[]>();
      for (const estateId in obj) {
        const estateAccess = obj[estateId]!;
        for (const userId in estateAccess) {
          const userAccess = estateAccess[userId]!;
          const vms = Object.values(userAccess).map((unit) => ViewModelHelper.unitAccessToVM(unit, estateId, this.ls)) ?? [];
          MapHlp.arrayMapAdd(userMap, userId, ...vms);
        }
      }
      AsyncHlp.updateBS(this._estateUserMap$, userMap);
    });
  }

  public getCompanies$(): Observable<CompanyData[]> {
    if (!this._companies$) {
      this._companies$ = new BehaviorSubject<CompanyData[]>([]);
      this.subscription$.add(this.adminService.getCompanies$().subscribe((companies) => this._companies$!.next(companies)));
    }
    return this._companies$;
  }

  public getCompany$(id: string): Observable<CompanyData | null> {
    return this.getCompanies$().pipe(map((x) => x.find((v) => v.id == id) ?? null));
  }

  public getRentalUnits$(estateId: string): Observable<Map<string, RentalUnit>> {
    let bs$ = this._units$.get(estateId);

    if (!bs$) {
      bs$ = new BehaviorSubject<Map<string, RentalUnit> | null>(null);
      this._units$.set(estateId, bs$);
      this.initUnits(estateId, bs$);
    }
    return bs$.pipe(filter((x) => !!x)) as any;
  }

  private initUnits(estateId: string, bs$: BehaviorSubject<Map<string, RentalUnit> | null>) {
    Promise.all([
      this.adminService.getApartments(estateId),
      this.adminService.getParkingSpots(estateId),
      this.adminService.getStorages(estateId)
    ]).then((collections) => {
      const map = new Map<string, RentalUnit>();
      collections.forEach((units) => units.forEach((unit) => map.set(unit.id, unit)));
      bs$.next(map);
    });
  }

  private prepareEmailsVM(): void {
    this.subscription$.add(
      combineLatest([this.adminService.getEmailDictionary$(), this.getEstates$()]).subscribe(async ([emails, estates]) => {
        const residentsColObj = await this.adminService.getAllResidentsColObj();
        const estateNameMap = MapHlp.fieldsMap<EstateData, string>(estates, 'id', 'name');
        const emailsVM: ResidentEmailVM[] = [];
        emails.forEach((emailDict) => {
          const anyDict = emailDict as any;
          for (const key in anyDict) {
            // ? key = estateId
            if (key != 'email') {
              const dictData = anyDict[key] as ContactDictData;
              const residentEstateColObj = residentsColObj[key];
              if (residentEstateColObj) {
                if (emailDict.email) {
                  emailsVM.push({
                    email: ParserHlp.deSanitizeKey(emailDict.email),
                    estate: estateNameMap.get(key) ?? `ESTATE ERROR: ${key}`,
                    estateId: key,
                    names: dictData.residentIds.map((id) => ToString.fullName(residentEstateColObj[id]))
                  });
                } else {
                  console.log(`Incorrect email ${emailDict.email}`);
                }
              }
            }
          }
        });
        AsyncHlp.updateBS(this._emails$, emailsVM);
      })
    );
  }

  private preparePhonesVM(): void {
    this.subscription$.add(
      combineLatest([this.adminService.getPhoneDictionary$(), this.getEstates$()]).subscribe(async ([phones, estates]) => {
        const residentsColObj = await this.adminService.getAllResidentsColObj();
        const estateNameMap = MapHlp.fieldsMap<EstateData, string>(estates, 'id', 'name');
        const phonesVM: ResidentPhoneVM[] = [];
        phones.forEach((phoneDict) => {
          const anyDict = phoneDict as any;
          for (const key in anyDict) {
            // ? key = estateId
            if (key != 'phone') {
              const dictData = anyDict[key] as ContactDictData;
              const residentEstateColObj = residentsColObj[key];
              if (residentEstateColObj) {
                if (phoneDict.phone) {
                  phonesVM.push({
                    phone: ParserHlp.sanitizeNumber(phoneDict.phone),
                    estate: estateNameMap.get(key) ?? `ESTATE ERROR: ${key}`,
                    estateId: key,
                    names: dictData.residentIds.map((id) => ToString.fullName(residentEstateColObj[id]))
                  });
                } else {
                  console.log(`Incorrect phone ${phoneDict.phone}`);
                }
              }
            }
          }
        });
        AsyncHlp.updateBS(this._phones$, phonesVM);
      })
    );
  }

  private prepareServiceUsersVM(): void {
    this.subscription$.add(
      combineLatest([
        this.subcontractorService.getSubcontractors$().pipe(idMap()),
        this.subcontractorService.getServiceUsers$(),
        this.getEstates$().pipe(idMap())
      ]).subscribe(async ([subcontractorsMap, serviceUsers, estateMap]) => {
        const eSubObj = await this.adminService.getAllEstateSubcontractorsColObj();
        const serviceUsersVM: ServiceUserVM[] = [];

        serviceUsers.forEach((su) => {
          const subId = su.subcontractorId ?? '';
          const sub = subcontractorsMap.get(subId);
          const name = ToString.fullName(su);
          // ? get estates from serviceUser

          /** ! common for both calls of buildEstateCoordVM - calls order is important */
          const parsedEstates = new Set<string>();
          const userEstates = buildEstateCoordVM(su.estates);
          const subEstates = buildEstateCoordVM(sub?.estates);

          serviceUsersVM.push({
            id: su.id,
            createDate: su.createDate,
            email: su.email ?? '',
            lastActivity: su.lastActivity || null,
            name,
            type: su.userType ?? UserType.NORMAL,
            subcontractor: sub || null,
            phoneNumber: su.phone ?? null,
            subEstates,
            userEstates
          });

          function buildEstateCoordVM(estateIds: string[] | Nullish): EstateCoordVM[] {
            const arr: EstateCoordVM[] = [];

            estateIds?.forEach((eId) => {
              if (parsedEstates.has(eId)) return;
              parsedEstates.add(eId);

              const estate = estateMap.get(eId);
              if (estate) {
                const eSub = ObjHlp.get(eSubObj[eId], subId);
                arr.push({
                  estateId: estate.id,
                  estateName: estate.name,
                  isCoordinator: !!eSub?.coordinators?.includes(su.id)
                });
              }
            });

            return arr;
          }
        });
        this._serviceUsers$.next(serviceUsersVM);
      })
    );
  }
}
